import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';
import { readAccessToken } from '../../utils/readAccessToken';
import { readBlobErrorMessage, triggerBlobDownload } from '../../utils/downloadBlob';
import { EXPORT_FALLBACK_FILENAME } from '../../config/exportConstants';

export interface DownloadReportExportVariables {
    exportId: string;
    /** The job resource's own `filename`, taken from the status response. */
    filename?: string;
}

/**
 * Authentication is a Bearer header, not a cookie, so an anchor or
 * `window.location` navigation to the download URL would arrive unauthenticated
 * and 401. The file is fetched as a blob instead and saved from memory.
 *
 * Naming comes from the job's `filename` — the backend now returns it on the
 * status response once the job succeeds, so the FE no longer reads
 * `Content-Disposition` (which is invisible to JS on this cross-origin response
 * unless the backend exposes it). The constant is a last-resort fallback.
 */
const downloadReportExport = async ({ exportId, filename }: DownloadReportExportVariables): Promise<void> => {
    try {
        const response = await axios(tpoGenUrls.downloadReportExport(exportId), {
            method: 'GET',
            responseType: 'blob',
            headers: { Authorization: `Bearer ${readAccessToken()}` },
        });
        triggerBlobDownload(response.data as Blob, filename || EXPORT_FALLBACK_FILENAME);
    } catch (error) {
        const data = (error as { response?: { data?: unknown } })?.response?.data;
        if (data instanceof Blob) {
            const message = await readBlobErrorMessage(data);
            throw new Error(message || (error as Error)?.message || '');
        }
        throw error;
    }
};

interface UseDownloadReportExportOptions {
    onSuccess?: (exportId: string) => void;
    onError?: (message: string, exportId: string) => void;
}

const useDownloadReportExportMutation = ({ onSuccess, onError }: UseDownloadReportExportOptions = {}) => {
    const { mutate, isPending } = useMutation({
        mutationFn: downloadReportExport,
        onSuccess: (_data, variables) => onSuccess?.(variables.exportId),
        onError: (error: unknown, variables) => onError?.((error as Error)?.message ?? '', variables.exportId),
    });

    return { downloadExport: mutate, isDownloading: isPending };
};

export default useDownloadReportExportMutation;
